import { on } from "@ngrx/store";
import { initialContactState, ContactState } from "./contact.state";
import { ContactActions } from "./contact.actions";
import { contactsReducer } from "./contacts/contacts.reducer";
import { PagesStore } from "../pages.state";

// **************************************************************************
// HELPER
// **************************************************************************

function withContact(state: PagesStore, updater: (contact: ContactState) => ContactState): PagesStore {
    return { ...state, contact: updater(state.contact) };
}

// **************************************************************************
// CONTACT REDUCER (array format for PagesStore)
// **************************************************************************

export const contactReducer = [
    // ACTIONS REDUCER - Clear error on action start
    on(
        ContactActions.initializeContact,
        ContactActions.updateContact,
        ContactActions.removeContact,
        ContactActions.addToCustomer,
        ContactActions.removeFromCustomer,
        (state: PagesStore) => withContact(state, contactState => ({
            ...contactState,
            error: null,
        })),
    ),

    // Initialize success
    on(
        ContactActions.initializeContactSuccess,
        (state: PagesStore, { contact }) => withContact(state, contactState => ({
            ...contactState,
            contact,
            error: null,
        })),
    ),

    // Update success
    on(ContactActions.updateContactSuccess, (state: PagesStore, { contact }) => withContact(state, contactState => ({
        ...contactState,
        contact,
        error: null,
    }))),

    // Remove success - reset to initial state
    on(
        ContactActions.removeContactSuccess,
        (state: PagesStore) => withContact(state, () => ({
            ...initialContactState,
            error: null,
        })),
    ),

    // Create success
    on(ContactActions.createContactSuccess, (state: PagesStore, { contact }) => withContact(state, contactState => ({
        ...contactState,
        contact,
        creating: false,
        error: null,
    }))),

    // Customer association success
    on(
        ContactActions.addToCustomerSuccess,
        ContactActions.removeFromCustomerSuccess,
        (state: PagesStore, { contact }) => withContact(state, contactState => ({
            ...contactState,
            contact,
            error: null,
        })),
    ),

    // Failure handlers
    on(
        ContactActions.initializeContactFailure,
        ContactActions.updateContactFailure,
        ContactActions.removeContactFailure,
        ContactActions.createContactFailure,
        ContactActions.addToCustomerFailure,
        ContactActions.removeFromCustomerFailure,
        (state: PagesStore, { error }) => withContact(state, contactState => ({
            ...contactState,
            error,
        })),
    ),

    // **************************************************************************
    // LOADING HANDLERS
    // **************************************************************************

    // Contact loading
    on(
        ContactActions.initializeContact,
        ContactActions.updateContact,
        ContactActions.addToCustomer,
        ContactActions.removeFromCustomer,
        (state: PagesStore) => withContact(state, contactState => ({
            ...contactState,
            loadingDetail: true,
        })),
    ),

    on(ContactActions.createContact, (state: PagesStore) => withContact(state, contactState => ({
        ...contactState,
        creating: true,
    }))),

    on(
        ContactActions.initializeContactSuccess,
        ContactActions.initializeContactFailure,
        ContactActions.updateContactSuccess,
        ContactActions.updateContactFailure,
        ContactActions.removeContactFailure,
        ContactActions.createContactSuccess,
        ContactActions.createContactFailure,
        ContactActions.addToCustomerSuccess,
        ContactActions.addToCustomerFailure,
        ContactActions.removeFromCustomerSuccess,
        ContactActions.removeFromCustomerFailure,
        (state: PagesStore) => withContact(state, contactState => ({
            ...contactState,
            loadingDetail: false,
            creating: false,
        })),
    ),

    // **************************************************************************
    // ATTIO SYNC HANDLERS
    // **************************************************************************
    on(ContactActions.syncFromAttio, (state: PagesStore) => withContact(state, contactState => ({
        ...contactState,
        syncingFromAttio: true,
    }))),

    on(ContactActions.syncFromAttioSuccess, ContactActions.syncFromAttioFailure, (state: PagesStore) => withContact(state, contactState => ({
        ...contactState,
        syncingFromAttio: false,
    }))),

    // **************************************************************************
    // CONTACTS LIST HANDLERS
    // **************************************************************************
    ...contactsReducer,
];
